import { useState, useEffect, useRef } from 'react';
import { fetchCapabilities, saveCapabilities, type Skill, type McpServer, type BuiltInTool, type McpTransport } from '../lib/data';
import { useOrg } from '../lib/org-context';
import { Skeleton } from './Skeleton';
import { DataTable, type Column, type FilterConfig, type RowAction } from './DataTable';
import { Modal } from './Modal';
import {
  Brain,
  Cpu,
  Wrench,
  BookOpen,
  Sparkles,
  Plus,
  Trash2,
  Pencil,
  CheckCircle2,
  RefreshCw,
  AlertCircle,
  Save,
  Lock,
  Code,
} from 'lucide-react';

const DEFAULT_SKILLS: Skill[] = [
  {
    name: 'pr-reviewer',
    description: 'Scans incoming pull requests, maps code changes against custom standards, and drafts context-aware review suggestions. Trigger on every opened or updated PR.',
    body: '# Autonomous PR Reviewer\n\n1. Summarize the diff and intent.\n2. Flag security, correctness, and test-coverage gaps.\n3. Post concise, actionable review comments with suggested fixes.',
    isActive: true,
  },
  {
    name: 'cve-audit',
    description: 'Inspects package lists and lockfiles against OSV/NVD and reports compliance alerts. Trigger when dependency manifests change.',
    body: '# CVE & SBOM Security Auditor\n\n- Parse package manifests + lockfiles.\n- Match packages against OSV/NVD advisories.\n- Report severity, fixed version, and remediation.',
    isActive: true,
  },
  {
    name: 'test-gen',
    description: 'Generates unit + integration test suites targeting the modified file segments. Trigger when source changes lack test coverage.',
    body: '# Automated Test Composer\n\n- Identify modified functions.\n- Emit vitest/jest tests covering edge cases.\n- Run the suite and report results.',
    isActive: false,
  },
];

const DEFAULT_MCP_SERVERS: McpServer[] = [
  {
    id: 'mcp-github',
    name: 'GitHub Connector',
    transport: 'stdio',
    isActive: true,
    command: 'npx',
    package: '-y @modelcontextprotocol/server-github',
    exportedTools: ['get_issue', 'create_comment', 'merge_pull_request'],
  },
  {
    id: 'mcp-linear',
    name: 'Linear Task Manager',
    transport: 'stdio',
    isActive: false,
    command: 'npx',
    package: '-y @modelcontextprotocol/server-linear',
    exportedTools: ['create_issue', 'update_task_status'],
  },
  {
    id: 'mcp-slack',
    name: 'Slack Notifier',
    transport: 'streamable_http',
    isActive: true,
    url: 'https://mcp-slack-prod.nim.ai/mcp',
    exportedTools: ['post_message', 'list_members'],
  },
];

const DEFAULT_BUILT_IN_TOOLS: BuiltInTool[] = [
  { id: 'view_file', name: 'view_file', description: 'Reads local source files with customizable pagination (up to 1600 lines at once).', usageCount: 1420, safetyTier: 'Auto-Run', category: 'Read' },
  { id: 'edit_file', name: 'edit_file', description: 'Applies surgical edits and patches to local source files via target block replacement.', usageCount: 841, safetyTier: 'Auto-Run', category: 'Write' },
  { id: 'run_command', name: 'run_command', description: 'Spawns local terminal execution for build, test, or lint checks.', usageCount: 429, safetyTier: 'Requires Admin Authorization', category: 'Execute' },
  { id: 'compile_applet', name: 'compile_applet', description: 'Orchestrates app compilation and build verification.', usageCount: 110, safetyTier: 'Auto-Run', category: 'Execute' },
];

const DEFAULT_INSTRUCTIONS = `# Nim AI Assistant Capabilities Instructions
1. **Security First**: Do not allow raw query strings or unescaped variables into SQL/HTML blocks.
2. **Framework Alignment**: Use TypeScript, functional components, standard Tailwind CSS.
3. **No Unrequested Packages**: Defer importing extra npm modules unless essential.
4. **Auto-Correction Loop**: If tests fail, inspect the stack trace, patch files, and compile again.`;

const inputCls = 'w-full bg-white border border-slate-200 rounded-md px-3 py-1.5 text-xs text-slate-700 focus:outline-none focus:border-purple-600';
const labelCls = 'text-[10px] font-sans font-bold text-slate-500 uppercase';

export function Capabilities() {
  const [activeSubTab, setActiveSubTab] = useState<'skills' | 'mcp' | 'builtin' | 'instructions'>('skills');
  const [skills, setSkills] = useState<Skill[]>([]);
  const [mcpServers, setMcpServers] = useState<McpServer[]>([]);
  const [builtInTools, setBuiltInTools] = useState<BuiltInTool[]>([]);
  const [instructions, setInstructions] = useState<string>('');
  const [loading, setLoading] = useState(true);

  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [isResetting, setIsResetting] = useState(false);

  // The current org scopes every fetch + save. Null while OrgProvider resolves
  // (or if the user has no orgs); we bail and wait rather than fetch with no
  // X-Org-Id (which would 400) or seed defaults into the wrong org.
  const { current } = useOrg();
  const orgId = current?.orgId ?? null;
  const canEdit = current?.role === 'OrgAdmin';

  // Ref always holding the org currently in view. Async writes (seed, restore)
  // persist to the org captured when they started, but only update the on-screen
  // state if the user is still viewing that org — otherwise switching orgs mid-
  // flight would overwrite the new org's data with the old org's defaults.
  const activeOrgRef = useRef<string | null>(null);
  useEffect(() => { activeOrgRef.current = orgId; }, [orgId]);

  const applyState = (data: { skills: Skill[]; mcpServers: McpServer[]; builtInTools: BuiltInTool[]; instructions: string }) => {
    setSkills(data.skills);
    setMcpServers(data.mcpServers);
    setBuiltInTools(data.builtInTools);
    setInstructions(data.instructions);
  };

  /** Persist `data` under `oid`, then update the view only if that org is still
   *  active. Guards against a seed/restore write landing after an org switch. */
  const applyAndPersist = async (data: { skills: Skill[]; mcpServers: McpServer[]; builtInTools: BuiltInTool[]; instructions: string }, oid: string) => {
    await saveCapabilities(data, oid);
    if (activeOrgRef.current === oid) applyState(data);
  };

  // Load (and reload on org switch). Keying on orgId guarantees switching orgs
  // refetches that org's doc instead of leaking the previous org's data into a
  // save under the new org. Explicit orgId is threaded into the fetch so it
  // doesn't race the OrgProvider bridge effect.
  useEffect(() => {
    if (!orgId) return;
    let cancelled = false;
    setLoading(true);
    setSaveError(null);
    // Drop any open editor — the draft's target row belongs to the previous org.
    setSkillDraft(null); setSkillEditing(null);
    setMcpDraft(null); setMcpEditingId(null);
    (async () => {
      try {
        const data = await fetchCapabilities(orgId);
        if (cancelled) return;
        const empty = data.skills.length === 0 && data.mcpServers.length === 0 && data.builtInTools.length === 0 && !data.instructions;
        // Only OrgAdmins can seed (PUT 403s for Members). Members see an empty
        // catalog and can ask an admin to seed it.
        if (empty && canEdit) {
          await applyAndPersist({ skills: DEFAULT_SKILLS, mcpServers: DEFAULT_MCP_SERVERS, builtInTools: DEFAULT_BUILT_IN_TOOLS, instructions: DEFAULT_INSTRUCTIONS }, orgId);
        } else if (!empty) {
          if (!cancelled) applyState(data);
        }
      } catch {
        // Worker offline (local dev) — fall back to defaults in-memory (not persisted).
        if (cancelled) return;
        applyState({ skills: DEFAULT_SKILLS, mcpServers: DEFAULT_MCP_SERVERS, builtInTools: DEFAULT_BUILT_IN_TOOLS, instructions: DEFAULT_INSTRUCTIONS });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId, canEdit]);

  const handleSaveAll = async () => {
    if (!orgId || loading) return;
    setSaveError(null);
    try {
      await saveCapabilities({ skills, mcpServers, builtInTools, instructions }, orgId);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 2500);
    } catch (e: any) {
      // Surface the server's validation message (first invalid skill/mcp field).
      setSaveError(e?.message ?? 'Save failed.');
    }
  };

  const handleRestoreDefaults = () => {
    if (!orgId) return;
    const oid = orgId;
    setIsResetting(true);
    setTimeout(async () => {
      try {
        await applyAndPersist({ skills: DEFAULT_SKILLS, mcpServers: DEFAULT_MCP_SERVERS, builtInTools: DEFAULT_BUILT_IN_TOOLS, instructions: DEFAULT_INSTRUCTIONS }, oid);
      } catch { /* offline */ }
      setIsResetting(false);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 2000);
    }, 500);
  };

  // ---------- mcp CRUD ----------
  const removeMcp = (id: string) => setMcpServers((prev) => prev.filter((s) => s.id !== id));
  const toggleMcp = (id: string) =>
    setMcpServers((prev) => prev.map((s) => (s.id === id ? { ...s, isActive: !s.isActive } : s)));

  const toggleBuiltInSafety = (id: string) =>
    setBuiltInTools((prev) => prev.map((t) => (t.id === id ? { ...t, safetyTier: t.safetyTier === 'Auto-Run' ? 'Requires Admin Authorization' : 'Auto-Run' } : t)));

  // ---------- modal editors (skills + mcp) ----------
  // Skills carry no id, so we snapshot the row object into a draft and resolve
  // its position in the full `skills` array by reference on commit (filtering
  // only changes the view, not array order, so indexOf is stable within a
  // session). If the snapshot is no longer in the array (org switch replaced
  // the rows, or the row was removed mid-edit) we drop the edit rather than
  // silently appending a duplicate — the modal also closes on org switch.
  const [skillDraft, setSkillDraft] = useState<Skill | null>(null);
  const [skillEditing, setSkillEditing] = useState<Skill | null>(null); // null = adding
  const openEditSkill = (s: Skill) => { setSkillEditing(s); setSkillDraft({ ...s }); };
  const openAddSkill = () => { setSkillEditing(null); setSkillDraft({ name: '', description: '', body: '', isActive: true }); };
  const commitSkillDraft = () => {
    if (!skillDraft) return;
    if (skillEditing && !skills.includes(skillEditing)) {
      setSkillDraft(null);
      setSkillEditing(null);
      return;
    }
    setSkills((prev) => {
      const idx = skillEditing ? prev.indexOf(skillEditing) : -1;
      if (idx === -1) return [...prev, skillDraft];
      return prev.map((s, i) => (i === idx ? skillDraft : s));
    });
    setSkillDraft(null);
    setSkillEditing(null);
  };

  const [mcpDraft, setMcpDraft] = useState<McpServer | null>(null);
  const [mcpEditingId, setMcpEditingId] = useState<string | null>(null); // null = adding
  const openEditMcp = (s: McpServer) => { setMcpEditingId(s.id); setMcpDraft({ ...s }); };
  const openAddMcp = () => {
    setMcpEditingId(null);
    setMcpDraft({ id: `mcp-${Date.now()}`, name: '', transport: 'stdio', isActive: true, command: 'npx', package: '', url: '', exportedTools: [] });
  };
  const commitMcpDraft = () => {
    if (!mcpDraft) return;
    setMcpServers((prev) => {
      const idx = mcpEditingId ? prev.findIndex((s) => s.id === mcpEditingId) : -1;
      if (idx === -1) return [...prev, mcpDraft];
      return prev.map((s, i) => (i === idx ? mcpDraft : s));
    });
    setMcpDraft(null);
    setMcpEditingId(null);
  };

  // ---------- DataTable columns / filters / actions ----------
  const skillColumns: Column<Skill>[] = [
    {
      key: 'skill',
      header: 'Skill',
      render: (s) => (
        <div className="space-y-0.5">
          <div className="flex items-center gap-2">
            <span className="font-mono text-xs font-semibold text-slate-800">{s.name || <span className="text-slate-400">unnamed</span>}</span>
            <button
              type="button"
              onClick={() => setSkills((prev) => { const i = prev.indexOf(s); return prev.map((x, idx) => idx === i ? { ...x, isActive: !x.isActive } : x); })}
              className={`text-[9px] font-mono font-bold px-1.5 py-0.5 rounded-full ${s.isActive ? 'bg-emerald-50 text-emerald-700 border border-emerald-100' : 'bg-slate-100 text-slate-400'}`}
              title={s.isActive ? 'Active — click to deactivate' : 'Inactive — click to activate'}
            >
              {s.isActive ? 'ACTIVE' : 'INACTIVE'}
            </button>
          </div>
        </div>
      ),
    },
    { key: 'desc', header: 'Description', render: (s) => <span className="text-[11px] text-slate-500">{s.description}</span> },
  ];
  const skillActions: RowAction<Skill>[] = [
    { key: 'edit', label: 'Edit skill', icon: Pencil, onClick: (s) => openEditSkill(s), title: 'Edit' },
    { key: 'delete', label: 'Remove skill', icon: Trash2, onClick: (s) => setSkills((prev) => prev.filter((x) => x !== s)), tone: 'danger', title: 'Remove' },
  ];

  const mcpColumns: Column<McpServer>[] = [
    {
      key: 'server',
      header: 'Server',
      render: (s) => (
        <div className="space-y-0.5">
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold text-slate-800">{s.name || <span className="text-slate-400">unnamed</span>}</span>
            <button
              type="button"
              onClick={() => toggleMcp(s.id)}
              className={`text-[9px] font-mono font-bold px-1.5 py-0.5 rounded-full ${s.isActive ? 'bg-emerald-50 text-emerald-700 border border-emerald-100' : 'bg-slate-100 text-slate-400'}`}
              title={s.isActive ? 'Active — click to deactivate' : 'Inactive — click to activate'}
            >
              {s.isActive ? 'ACTIVE' : 'INACTIVE'}
            </button>
          </div>
        </div>
      ),
    },
    { key: 'transport', header: 'Transport', width: '120px', render: (s) => <span className="text-[11px] font-mono text-slate-600">{s.transport}</span> },
    { key: 'conn', header: 'Connection', render: (s) => <span className="text-[11px] font-mono text-slate-500 truncate">{s.transport === 'stdio' ? `${s.command ?? 'npx'} ${s.package ?? ''}` : (s.url ?? '—')}</span> },
    { key: 'tools', header: 'Tools', width: '70px', render: (s) => <span className="text-[11px] font-mono text-slate-500">{s.exportedTools?.length ?? 0}</span> },
  ];
  const mcpActions: RowAction<McpServer>[] = [
    { key: 'edit', label: 'Edit server', icon: Pencil, onClick: (s) => openEditMcp(s), title: 'Edit' },
    { key: 'delete', label: 'Remove server', icon: Trash2, onClick: (s) => removeMcp(s.id), tone: 'danger', title: 'Remove' },
  ];

  const builtinColumns: Column<BuiltInTool>[] = [
    {
      key: 'tool',
      header: 'Tool',
      render: (t) => (
        <div className="space-y-0.5">
          <div className="flex items-center gap-2">
            <span className="p-1 bg-slate-100 rounded text-slate-600"><Code className="w-3.5 h-3.5" /></span>
            <span className="text-xs font-mono font-bold text-slate-800">{t.name}</span>
            <span className="text-[9px] uppercase font-mono font-bold px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">{t.category}</span>
          </div>
          <p className="text-[11px] text-slate-500 leading-normal max-w-xl">{t.description}</p>
        </div>
      ),
    },
    { key: 'usage', header: 'Usages', width: '90px', render: (t) => <span className="text-xs font-bold text-slate-700 font-mono">{t.usageCount}</span> },
    {
      key: 'safety',
      header: 'Safety',
      width: '180px',
      render: (t) => (
        <span className={`text-[10px] font-sans font-bold flex items-center gap-1 px-2 py-0.5 rounded ${t.safetyTier === 'Auto-Run' ? 'bg-emerald-50 text-emerald-700 border border-emerald-100' : 'bg-amber-50 text-amber-700 border border-amber-100'}`}>
          {t.safetyTier === 'Auto-Run' ? <><CheckCircle2 className="w-3 h-3 text-emerald-600" /> Auto-Run</> : <><Lock className="w-3 h-3 text-amber-600" /> Authorization</>}
        </span>
      ),
    },
  ];
  const builtinFilters: FilterConfig<BuiltInTool>[] = [
    { key: 'category', label: 'Category', options: [{ value: 'Read', label: 'Read' }, { value: 'Write', label: 'Write' }, { value: 'Execute', label: 'Execute' }], predicate: (t, v) => t.category === v },
    { key: 'safety', label: 'Safety', options: [{ value: 'Auto-Run', label: 'Auto-Run' }, { value: 'Requires Admin Authorization', label: 'Authorization' }], predicate: (t, v) => t.safetyTier === v },
  ];
  const builtinActions: RowAction<BuiltInTool>[] = [
    { key: 'toggle', label: 'Change safety mode', onClick: (t) => toggleBuiltInSafety(t.id), title: 'Change safety mode' },
  ];

  if (loading) {
    return (
      <div className="space-y-8 animate-fadeIn" aria-busy="true" aria-label="Loading capabilities">
        {/* Hero bar */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 border-b border-[rgba(55,53,47,0.08)] pb-6">
          <Skeleton width={260} height={14} rounded={3} />
          <div className="flex items-center gap-3">
            <Skeleton width={140} height={36} rounded={8} />
            <Skeleton width={160} height={36} rounded={8} />
          </div>
        </div>
        {/* Grid: nav + details */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          <div className="lg:col-span-1 space-y-2">
            <Skeleton width={150} height={10} rounded={3} />
            <div className="space-y-1.5">
              {[0, 1, 2, 3].map((i) => (
                <Skeleton key={i} width="100%" height={28} rounded={8} />
              ))}
            </div>
          </div>
          <div className="lg:col-span-3 min-h-[480px] bg-white border border-slate-200/80 rounded-xl p-6 shadow-sm space-y-4">
            <Skeleton width={220} height={14} rounded={3} />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {[0, 1, 2, 3].map((i) => (
                <div key={i} className="border border-slate-200/70 rounded-lg p-3 space-y-2" aria-hidden="true">
                  <Skeleton width="60%" height={12} rounded={3} />
                  <Skeleton width="90%" height={10} rounded={3} />
                  <Skeleton width="40%" height={10} rounded={3} />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8" id="capabilities-view-wrapper">

      {/* Brand Hero Heading */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 border-b border-[rgba(55,53,47,0.08)] pb-6" id="capabilities-hero-bar">
        <div className="space-y-1">
          <div className="flex items-center gap-2 text-purple-600">
            <Sparkles className="w-4 h-4 animate-pulse" />
            <span className="font-mono text-xs font-bold tracking-tight uppercase text-slate-800">AI Agent Capabilities & Tools</span>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-3 shrink-0">
          <button
            onClick={handleRestoreDefaults}
            disabled={isResetting || !canEdit}
            title={canEdit ? 'Restore defaults' : 'Only an Org Admin can change capabilities'}
            className="flex items-center gap-1.5 border border-slate-200 hover:border-slate-300 text-slate-500 hover:text-slate-700 bg-white px-3.5 py-2 rounded-lg text-xs font-semibold shadow-xs hover:shadow-sm transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${isResetting ? 'animate-spin' : ''}`} />
            <span>Restore Defaults</span>
          </button>
          <button
            onClick={handleSaveAll}
            disabled={!canEdit}
            title={canEdit ? 'Save capabilities' : 'Only an Org Admin can change capabilities'}
            className="flex items-center gap-1.5 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white px-4 py-2 rounded-lg text-xs font-bold shadow-md shadow-purple-500/10 hover:shadow-lg transition transform hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none disabled:hover:shadow-md"
          >
            <Save className="w-3.5 h-3.5" />
            <span>Save Capabilities</span>
          </button>
        </div>
      </div>

      {!canEdit && (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 text-xs px-4 py-3 rounded-lg flex items-center gap-2">
          <AlertCircle className="w-4 h-4 text-amber-600 shrink-0" />
          <span>You are a Member of this organization — capabilities are read-only. Ask an Org Admin to make changes.</span>
        </div>
      )}
      {saveSuccess && (
        <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs px-4 py-3 rounded-lg flex items-center gap-2" id="success-banner">
          <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
          <span className="font-semibold">Capabilities updated successfully!</span> Changes are linked to the active agent profile for this organization.
        </div>
      )}
      {saveError && (
        <div className="bg-rose-50 border border-rose-200 text-rose-800 text-xs px-4 py-3 rounded-lg flex items-start gap-2">
          <AlertCircle className="w-4 h-4 text-rose-600 shrink-0 mt-0.5" />
          <div>
            <span className="font-semibold">Save failed:</span>{saveError}
            <div className="mt-0.5 text-rose-600/80">Skill names must be kebab-case. MCP transport must be stdio (npx/uvx + package) or streamable_http (url).</div>
          </div>
        </div>
      )}

      {/* Grid Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-8" id="capabilities-view-grid">

        {/* Navigation Column */}
        <div className="lg:col-span-1 flex flex-col gap-0.5 shrink-0" id="capabilities-sub-navigation">
          <div className="text-[10px] font-sans font-bold text-[rgba(55,53,47,0.5)] uppercase tracking-wider px-2 mb-2">Capabilities Menu</div>

          {([
            { id: 'skills', label: 'Skills', icon: Brain },
            { id: 'mcp', label: 'MCP Tools', icon: Cpu },
            { id: 'builtin', label: 'Built-in Tools', icon: Wrench },
            { id: 'instructions', label: 'Instructions', icon: BookOpen },
          ] as const).map((tab) => {
            const TabIcon = tab.icon;
            const active = activeSubTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveSubTab(tab.id)}
                className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-[4px] text-xs font-medium font-sans transition text-left ${
                  active ? 'bg-[rgba(55,53,47,0.08)] text-[#37352F]' : 'text-[#37352F] hover:bg-[rgba(55,53,47,0.04)]'
                }`}
              >
                <TabIcon className={`w-[14px] h-[14px] shrink-0 ${active ? 'text-[#37352F]' : 'text-[rgba(55,53,47,0.5)]'}`} />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </div>

        {/* Details Column */}
        <div className="lg:col-span-3 min-h-[480px] bg-white border border-slate-200/80 rounded-xl p-6 shadow-sm" id="capabilities-details-panel">

          {/* TAB 1: SKILLS (SKILL.md standard) */}
          {activeSubTab === 'skills' && (
            <div className="space-y-4 animate-fadeIn">
              <div className="border-b border-slate-100 pb-4 space-y-1">
                <h3 className="text-base font-bold text-slate-800">Agent Skills <span className="text-[10px] font-mono text-slate-400">(SKILL.md standard)</span></h3>
                <p className="text-[11px] text-slate-500">Each skill is a name + trigger description + markdown body. Names are kebab-case.</p>
              </div>
              <DataTable
                rows={skills}
                columns={skillColumns}
                rowKey={(s) => s.name || 'unnamed'}
                searchKeys={[(s) => s.name, (s) => s.description]}
                searchPlaceholder="Search skills…"
                actions={skillActions}
                pageSize={10}
                emptyState={<>No skills yet. Click <strong>Add Skill</strong>.</>}
                toolbar={
                  <button onClick={openAddSkill} className="flex items-center gap-1.5 bg-purple-600 hover:bg-purple-700 text-white text-xs font-bold px-3 py-1.5 rounded-md shrink-0">
                    <Plus className="w-3.5 h-3.5" /> Add Skill
                  </button>
                }
              />
            </div>
          )}

          {/* TAB 2: MCP SERVERS */}
          {activeSubTab === 'mcp' && (
            <div className="space-y-4 animate-fadeIn">
              <div className="border-b border-slate-100 pb-4 space-y-1">
                <h3 className="text-base font-bold text-slate-800">Model Context Protocol (MCP) Connections</h3>
                <p className="text-[11px] text-slate-500">Two transports: <strong>stdio</strong> (run via npx/uvx) or <strong>streamable_http</strong> (remote URL).</p>
              </div>
              <DataTable
                rows={mcpServers}
                columns={mcpColumns}
                rowKey={(s) => s.id}
                searchKeys={[(s) => s.name, (s) => s.package ?? '', (s) => s.url ?? '']}
                searchPlaceholder="Search servers…"
                filters={[{ key: 'transport', label: 'Transport', options: [{ value: 'stdio', label: 'stdio' }, { value: 'streamable_http', label: 'streamable_http' }], predicate: (s, v) => s.transport === v }]}
                actions={mcpActions}
                pageSize={10}
                emptyState={<>No MCP servers. Click <strong>Add Server</strong>.</>}
                toolbar={
                  <button onClick={openAddMcp} className="flex items-center gap-1.5 bg-purple-600 hover:bg-purple-700 text-white text-xs font-bold px-3 py-1.5 rounded-md shrink-0">
                    <Plus className="w-3.5 h-3.5" /> Add Server
                  </button>
                }
              />
            </div>
          )}

          {/* TAB 3: BUILT-IN TOOLS */}
          {activeSubTab === 'builtin' && (
            <div className="space-y-4 animate-fadeIn">
              <div className="border-b border-slate-100 pb-4 space-y-1">
                <h3 className="text-base font-bold text-slate-800">Built-in Core Actions & Safety</h3>
                <p className="text-[11px] text-slate-500">Regulate confirmation thresholds on filesystem and terminal executions.</p>
              </div>
              <DataTable
                rows={builtInTools}
                columns={builtinColumns}
                rowKey={(t) => t.id}
                searchKeys={[(t) => t.name, (t) => t.description]}
                searchPlaceholder="Search tools…"
                filters={builtinFilters}
                actions={builtinActions}
                pageSize={10}
                emptyState={<>No built-in tools.</>}
              />
            </div>
          )}

          {/* TAB 4: SYSTEM INSTRUCTIONS */}
          {activeSubTab === 'instructions' && (
            <div className="space-y-6 animate-fadeIn">
              <div className="border-b border-slate-100 pb-4 space-y-1">
                <h3 className="text-base font-bold text-slate-800">System Instruction Templates</h3>
                <p className="text-[11px] text-slate-500">Inject context guidelines, style guides, and response bounds.</p>
              </div>
              <div className="space-y-4">
                <div className="space-y-1.5">
                  <div className="text-[10px] font-sans font-bold text-slate-400 uppercase tracking-wider">Quick Inject Templates</div>
                  <div className="flex flex-wrap gap-2">
                    <button type="button" onClick={() => setInstructions((p) => p + '\n# Enforce Strict Clean Architecture\n- Group by business concerns instead of layers.')} className="text-[10px] font-sans font-semibold text-slate-600 border border-slate-200 hover:border-purple-600 hover:text-purple-600 bg-white px-2 py-1 rounded-md transition">+ Clean Architecture</button>
                    <button type="button" onClick={() => setInstructions((p) => p + '\n# High-Performance Directives\n- Use streams for payloads larger than 5MB.')} className="text-[10px] font-sans font-semibold text-slate-600 border border-slate-200 hover:border-purple-600 hover:text-purple-600 bg-white px-2 py-1 rounded-md transition">+ High Performance</button>
                    <button type="button" onClick={() => setInstructions((p) => p + '\n# Strict Security Guardrails\n- Reject any PR introducing direct script/HTML binding.')} className="text-[10px] font-sans font-semibold text-slate-600 border border-slate-200 hover:border-purple-600 hover:text-purple-600 bg-white px-2 py-1 rounded-md transition">+ Secure Guardrails</button>
                  </div>
                </div>
                <div className="space-y-1.5 pt-2">
                  <label className={labelCls}>Enforced System Prompts</label>
                  <textarea rows={12} value={instructions} onChange={(e) => setInstructions(e.target.value)} className="w-full bg-slate-50 border border-slate-200 focus:border-purple-600 focus:bg-white rounded-lg p-4 font-mono text-xs text-slate-700 leading-relaxed focus:outline-none transition shadow-inner" placeholder="# Customize instructions here..." />
                </div>
                <div className="bg-slate-50 border border-slate-200/60 rounded-lg p-3.5 flex items-start gap-2.5 text-[11px] text-slate-500">
                  <AlertCircle className="w-4 h-4 text-purple-600 shrink-0 mt-0.5" />
                  <div className="space-y-1">
                    <span className="font-bold text-slate-700">How to write clean directives:</span>
                    <p className="leading-relaxed">Keep statements directive rather than explanatory. Bulleted items work well for defining absolute coding barriers.</p>
                  </div>
                </div>
              </div>
            </div>
          )}

        </div>
      </div>

      {/* Skill editor modal (add / edit) */}
      <Modal
        open={skillDraft !== null}
        onClose={() => { setSkillDraft(null); setSkillEditing(null); }}
        title={skillEditing ? 'Edit skill' : 'Add skill'}
        subtitle="Writes to the draft — click Save Capabilities to persist."
        size="md"
        footer={
          <>
            <button onClick={() => { setSkillDraft(null); setSkillEditing(null); }} className="px-3 py-1.5 rounded-md border border-slate-200 text-slate-600 text-xs font-semibold">Cancel</button>
            <button onClick={commitSkillDraft} disabled={!skillDraft?.name.trim()} className="px-3 py-1.5 rounded-md bg-purple-600 hover:bg-purple-700 text-white text-xs font-bold disabled:opacity-50">{skillEditing ? 'Apply' : 'Add'}</button>
          </>
        }
      >
        {skillDraft && (
          <div className="space-y-3">
            <div>
              <label className={labelCls}>Name (kebab-case)</label>
              <input value={skillDraft.name} onChange={(e) => setSkillDraft({ ...skillDraft, name: e.target.value })} placeholder="skill-name" className={`${inputCls} font-mono`} autoFocus />
            </div>
            <div>
              <label className={labelCls}>Description (trigger)</label>
              <textarea rows={3} value={skillDraft.description} onChange={(e) => setSkillDraft({ ...skillDraft, description: e.target.value })} className={`${inputCls} leading-relaxed`} placeholder="When this skill should activate…" />
            </div>
            <div>
              <label className={labelCls}>Body (markdown instructions)</label>
              <textarea rows={6} value={skillDraft.body} onChange={(e) => setSkillDraft({ ...skillDraft, body: e.target.value })} className={`${inputCls} font-mono leading-relaxed`} placeholder="# Skill instructions…" />
            </div>
            <label className="flex items-center gap-2 text-xs text-slate-700 cursor-pointer">
              <input type="checkbox" checked={skillDraft.isActive} onChange={(e) => setSkillDraft({ ...skillDraft, isActive: e.target.checked })} className="accent-purple-600" />
              Active
            </label>
          </div>
        )}
      </Modal>

      {/* MCP server editor modal (add / edit) */}
      <Modal
        open={mcpDraft !== null}
        onClose={() => { setMcpDraft(null); setMcpEditingId(null); }}
        title={mcpEditingId ? 'Edit MCP server' : 'Add MCP server'}
        subtitle="Writes to the draft — click Save Capabilities to persist."
        size="md"
        footer={
          <>
            <button onClick={() => { setMcpDraft(null); setMcpEditingId(null); }} className="px-3 py-1.5 rounded-md border border-slate-200 text-slate-600 text-xs font-semibold">Cancel</button>
            <button onClick={commitMcpDraft} disabled={!mcpDraft?.name.trim()} className="px-3 py-1.5 rounded-md bg-purple-600 hover:bg-purple-700 text-white text-xs font-bold disabled:opacity-50">{mcpEditingId ? 'Apply' : 'Add'}</button>
          </>
        }
      >
        {mcpDraft && (
          <div className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Server name</label>
                <input value={mcpDraft.name} onChange={(e) => setMcpDraft({ ...mcpDraft, name: e.target.value })} placeholder="e.g. GitHub Connector" className={inputCls} autoFocus />
              </div>
              <div>
                <label className={labelCls}>Transport</label>
                <select value={mcpDraft.transport} onChange={(e) => setMcpDraft({ ...mcpDraft, transport: e.target.value as McpTransport })} className={inputCls}>
                  <option value="stdio">stdio (npx / uvx)</option>
                  <option value="streamable_http">streamable_http</option>
                </select>
              </div>
            </div>
            {mcpDraft.transport === 'stdio' ? (
              <div className="grid grid-cols-1 sm:grid-cols-[120px_1fr] gap-3">
                <div>
                  <label className={labelCls}>Launcher</label>
                  <select value={mcpDraft.command ?? 'npx'} onChange={(e) => setMcpDraft({ ...mcpDraft, command: e.target.value as 'npx' | 'uvx' })} className={inputCls}>
                    <option value="npx">npx</option>
                    <option value="uvx">uvx</option>
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Package + args</label>
                  <input value={mcpDraft.package ?? ''} onChange={(e) => setMcpDraft({ ...mcpDraft, package: e.target.value })} placeholder="-y @modelcontextprotocol/server-github" className={`${inputCls} font-mono`} />
                </div>
              </div>
            ) : (
              <div>
                <label className={labelCls}>URL</label>
                <input value={mcpDraft.url ?? ''} onChange={(e) => setMcpDraft({ ...mcpDraft, url: e.target.value })} placeholder="https://mcp.example.com/mcp" className={`${inputCls} font-mono`} />
              </div>
            )}
            <div>
              <label className={labelCls}>Exported tools (comma-separated)</label>
              <input
                value={(mcpDraft.exportedTools ?? []).join(', ')}
                onChange={(e) => setMcpDraft({ ...mcpDraft, exportedTools: e.target.value.split(',').map((t) => t.trim()).filter(Boolean) })}
                placeholder="get_issue, create_comment"
                className={`${inputCls} font-mono`}
              />
            </div>
            <label className="flex items-center gap-2 text-xs text-slate-700 cursor-pointer">
              <input type="checkbox" checked={mcpDraft.isActive} onChange={(e) => setMcpDraft({ ...mcpDraft, isActive: e.target.checked })} className="accent-purple-600" />
              Active
            </label>
          </div>
        )}
      </Modal>
    </div>
  );
}
